import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/shared/EmptyState'
import { SearchEmptyState } from '@/components/shared/SearchEmptyState'
import { SearchBar } from '@/components/shared/SearchBar'
import { ItemCard } from '@/components/shared/ItemCard'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { ErrorState } from '@/components/shared/ErrorState'
import { useListQuery } from '@/hooks/useListQuery'
import { useSeries } from '@/hooks/useSeriesList'
import type {
  SeriesWithRelations,
  SeriesMoveWithRelations,
} from '@/types/api-extensions'

export function SeriesArchivePage() {
  const navigate = useNavigate()
  const listQuery = useListQuery()

  // Fetch series with search, pagination, and relations for thumbnails
  const { data, isLoading, isError, error, refetch } = useSeries({
    ...listQuery.filters,
    includeRelations: true,
  })

  // Derive states
  const totalSeries = data?.total ?? 0
  const hasSearch = Boolean(listQuery.searchText)
  const hasSeries = totalSeries > 0
  const noSeries = totalSeries === 0

  const isEmpty = !isLoading && noSeries && !hasSearch
  const isSearchEmpty = !isLoading && noSeries && hasSearch

  const handleCreateSeries = () => {
    navigate('/series/new')
  }

  const handleSeriesClick = (seriesId: string) => {
    navigate(`/series/${seriesId}`)
  }

  /**
   * Get thumbnail from first move in series
   */
  const getSeriesThumbnail = (
    series: SeriesWithRelations
  ): string | undefined => {
    if (!series.seriesMoves || series.seriesMoves.length === 0) {
      return undefined
    }

    // Get first move with thumbnail from series
    const activeMoves = series.seriesMoves.filter(
      (sm: SeriesMoveWithRelations) => sm.move?.thumbnailUrl
    )

    for (const seriesMove of activeMoves) {
      if (seriesMove.move?.thumbnailUrl) {
        return seriesMove.move.thumbnailUrl
      }
    }

    return undefined
  }

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Main Content */}
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {/* Search Bar + Create Button */}
        {!isEmpty && (
          <div className="mb-6 flex items-center gap-4">
            <div className="flex-1">
              <SearchBar
                value={listQuery.searchText}
                onChange={listQuery.setSearchText}
                placeholder="Search series..."
                ariaLabel="Search series"
                className="max-w-md"
              />
            </div>
            <Button
              onClick={handleCreateSeries}
              size="sm"
              className="bg-[#0033A0] hover:bg-[#002080]"
              aria-label="Create new series"
            >
              + Create Series
            </Button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && <LoadingSpinner size="lg" />}

        {/* Error State */}
        {isError && (
          <ErrorState
            error={error || new Error('Failed to load series')}
            onRetry={refetch}
            message="Failed to load series"
          />
        )}

        {/* Empty State */}
        {isEmpty && (
          <EmptyState
            icon="📚"
            title="No series yet"
            description="Create your first series to organize your moves into sequences"
            actionLabel="Create First Series"
            onAction={handleCreateSeries}
          />
        )}

        {/* Search Empty State */}
        {!isLoading && !isError && isSearchEmpty && (
          <SearchEmptyState
            searchText={listQuery.searchText}
            onClearSearch={listQuery.clearSearch}
            resourceType="series"
          />
        )}

        {/* Series Grid */}
        {!isLoading && !isError && hasSeries && !isSearchEmpty && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data?.series.map(s => {
              const seriesWithRelations = s as SeriesWithRelations
              return (
                <ItemCard
                  key={s.id}
                  title={s.name}
                  description={s.description}
                  imageUrl={getSeriesThumbnail(seriesWithRelations)}
                  imagePlaceholder="📚"
                  metadata={
                    <div className="text-xs text-[#666666]">
                      Created {formatDate(new Date(s.createdAt))}
                    </div>
                  }
                  onClick={() => handleSeriesClick(s.id)}
                  ariaLabel={`View series: ${s.name}`}
                />
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}

function formatDate(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'today'
  if (diffDays === 1) return 'yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
  return date.toLocaleDateString()
}
